import { inject, Injectable } from '@angular/core';
import { Transaction, Span } from '@elastic/apm-rum';
import { ApmService } from '@elastic/apm-rum-angular';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { filter, tap } from 'rxjs/operators';

interface Entity {
  message: string;
  moduleName: string;
  labels: Labels;
}

@Injectable({ providedIn: 'root' })
export class InteractionTrackingService {
  private readonly apmService: ApmService = inject(ApmService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private currentTransaction: Transaction | null = null;
  private currentSpan: Span | null = null;

  private lastTransactionName: string | null = null;

  private currentEntity: Entity | null = null;

  private inactivityTimer: NodeJS.Timeout = null;
  private readonly INACTIVITY_TIMEOUT = 30000;

  private readonly trackedTags = [
    'button',
    'a',
    '.ng-option',
    '.dropdown-item',
    'ng-select',
    'sk-select',
    'input[type=file]',
    'mat-tree-node',
    'mat-nested-tree-node',
    'mat-checkbox',
    'div[role=columnheader]',
    'div[role=tab]',
  ];

  private buildEntity(url: string, queryParams: Params): Entity {
    const currentModuleName = this.extractModuleName(url);
    const urlSegments = this.extractUrlSegments(url);
    const extraLabels: Labels = {
      browserWidth: window.innerWidth,
      browserHeight: window.innerHeight,
    };
    let message = '';

    switch (currentModuleName) {
      case 'dynamic-forms': {
        const isListView = this.extractSegmentName(urlSegments, 2) === 'documents';
        if (isListView) {
          extraLabels['template'] = this.extractSegmentName(urlSegments, 1);
          extraLabels['templateId'] = this.extractSegmentName(urlSegments, 3);
          extraLabels['project'] = this.extractSegmentName(urlSegments, 5);
          message = `List view of ${extraLabels['template']}`;
        } else {
          const isEditView = !!queryParams?.['edit'];
          extraLabels['pageCode'] = queryParams?.['pageCode'];
          message = `${isEditView ? 'Edit' : 'Read'} document ${this.extractSegmentName(urlSegments, 1)}`;
        }
        break;
      }

      case 'projects': {
        const param = this.extractSegmentName(urlSegments, 1);
        const isEdit = this.extractSegmentName(urlSegments, 2) === 'edit';
        const isProjectList = param !== 'new' && Number.isNaN(parseInt(param, 10));
        if (isProjectList) {
          message = `List of ${param} projects`;
        } else if (param === 'new') {
          message = 'Create new project';
        } else {
          message = `${isEdit ? 'Edit' : 'Read'} project ${param}`;
        }
        break;
      }

      case 'configuration': {
        const partition = this.extractSegmentName(urlSegments, 1);
        const subPartition = this.extractSegmentName(urlSegments, 2);
        if (partition) {
          message = `Configuration of ${partition}`;
          if (subPartition) {
            message += `/${subPartition}`;
          }
        } else {
          message = 'Configurations';
        }
        break;
      }

      default:
        message = `Open ${currentModuleName}`;
        break;
    }

    return {
      message,
      moduleName: currentModuleName,
      labels: extraLabels,
    };
  }

  private resetInactivityTimer(): void {
    clearTimeout(this.inactivityTimer);

    this.inactivityTimer = setTimeout(() => {
      this.currentSpan?.end();
      this.currentTransaction?.end();

      this.currentSpan = null;
      this.currentTransaction = null;
      this.lastTransactionName = null;
    }, this.INACTIVITY_TIMEOUT);
  }

  private refreshTransaction(entity: Entity): void {
    if(this.lastTransactionName !== entity.message || !this.currentTransaction) {
      this.currentTransaction?.end();

      this.currentTransaction = this.apmService.apm.startTransaction(`${entity.message}`, 'user-interaction');
      this.currentTransaction.addLabels(entity.labels);
    }

    this.lastTransactionName = entity.message;
    this.resetInactivityTimer();
  }

  initClickTracking(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      tap((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.currentEntity = this.buildEntity(url, queryParams);
        this.refreshTransaction(this.currentEntity);
      }),
    ).subscribe();

    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const clickedElement = target.closest(this.trackedTags.join(', '));
      if (!clickedElement) return;

      const label = this.buildMessage(clickedElement as HTMLElement);
      if (!label) return;

      if(!this.currentTransaction) {
        this.refreshTransaction(this.currentEntity);
      }

      this.currentSpan?.end();
      this.currentSpan = this.currentTransaction?.startSpan(`Click ${label}`, 'user-interaction');
      this.resetInactivityTimer();
    }, true);
  }

  private extractUrlSegments(url: string): string[] {
    const path = url.split('?')[0];
    return path.split('/').filter(Boolean);
  }

  private extractSegmentName(segments: string[], level: number): string | null {
    return segments.length > level ? segments[level] : null;
  }

  private extractModuleName(url: string): string | null {
    const segments = this.extractUrlSegments(url);

    return this.extractSegmentName(segments, 0);
  }

  private buildMessage(el: HTMLElement): string | null {
    const type = this.getClickableElementType(el);
    if (!type) return null;

    const dataTest = el.getAttribute('data-test');
    const ariaLabel = el.getAttribute('aria-label');
    const role = el.getAttribute('role');
    const ariaChecked = el.getAttribute('aria-checked');
    const tooltip = el.getAttribute('ng-reflect-message');
    const innerText = el.innerText?.replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ').trim();
    const href = (el as HTMLAnchorElement)?.href;
    const icon = this.getElementIcon(el);
    const id = el.id;

    const parts: string[] = [];
    if (dataTest) parts.push(`[data-test="${dataTest}"]`);
    if (!dataTest && ariaLabel) parts.push(`[aria-label="${ariaLabel}"]`);
    if (!dataTest && role) parts.push(`[role="${role}"]`);
    if (ariaChecked) parts.push(`[aria-checked="${ariaChecked}"]`);
    if (!dataTest && !ariaLabel && !innerText && !icon && href) parts.push(`[href="${href}"]`);
    if (id) parts.push(`[id="${id}"]`);

    if (['ng-select', 'sk-select'].includes(type)) {
      const placeholder = el.getAttribute('placeholder');
      const value = el.querySelector('.ng-value')?.textContent?.trim();
      if (placeholder) parts.push(`[placeholder="${placeholder}"]`);
      else if (value) parts.push(`[value="${value}"]`);
    } else if (type === 'mat-nested-tree-node') {
      const value = el.querySelector('.tree-list__category-title')?.textContent?.trim();
      if (value) parts.push(`[value="${value}"]`);
    } else if (innerText) {
      parts.push(`[text="${this.truncateMiddle(innerText)}"]`);
    }

    if (!innerText && icon) parts.push(`[icon="${icon}"]`);
    if (!innerText && tooltip) parts.push(`[tooltip="${tooltip}"]`);

    if (type === 'mat-checkbox') {
      const checked = el.classList.toString().includes('checked');
      parts.push(`[aria-checked="${checked}"]`);
    }

    if (type === 'column-header') {
      const colId = el.getAttribute('col-id');
      const ariaSort = el.getAttribute('aria-sort');
      if (colId) parts.push(`[col-id="${colId}"]`);
      if (ariaSort) parts.push(`[aria-sort="${ariaSort}"]`);
    }

    return `${type.toUpperCase()}${parts.join('')}`;
  }

  private getClickableElementType(el: HTMLElement): string | null {
    for (const selector of this.trackedTags) {
      if (selector.startsWith('.')) {
        const className = selector.slice(1);
        if (el.classList.contains(className)) return className;
      } else if (selector.includes('[')) {
        const [tag, attrPart] = selector.split('[');
        const attr = attrPart.replace(']', '');
        const [attrName, attrValue] = attr.split('=');
        if (
          el.tagName.toLowerCase() === tag.toLowerCase() &&
          el.getAttribute(attrName) === attrValue?.replace(/['"]/g, '')
        ) {
          if (selector === 'div[role=columnheader]') return 'column-header';
          if (selector === 'div[role=tab]') return 'tab';
          return selector;
        }
      } else if (el.tagName.toLowerCase() === selector.toLowerCase()) {
        return selector;
      }
    }
    return null;
  }

  private getElementIcon(el: HTMLElement): string {
    return el.querySelector('i, .icon')?.classList.toString()
      .replace(/\bng-[^\s]+/g, '')
      .replace(/\bmat-mdc-[^\s]+/g, '')
      .replace(/\s+/g, ' ')
      .trim() || '';
  }

  private truncateMiddle(str: string, maxLength = 64): string {
    const ellipsis = '...';
    if (str.length <= maxLength) return str;
    const keep = maxLength - ellipsis.length;
    const half = Math.floor(keep / 2);
    return `${str.slice(0, half)}${ellipsis}${str.slice(str.length - (keep - half))}`;
  }

  getCurrentTransaction(): Transaction | null {
    return this.currentTransaction;
  }

  getCurrentSpan(): Span | null {
    return this.currentSpan;
  }
}
